'use client';
import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

const setCookie = (name: string, value: string, options?: Record<string, unknown>) => {
  if (typeof document === 'undefined') return;
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=Lax`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires as string).toUTCString()}`;
  document.cookie = s;
};

const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
};

const fromCookies = () => {
  if (typeof document === 'undefined') return [];
  return document.cookie
    .split(';')
    .filter(Boolean)
    .map((c) => {
      const eqIdx = c.trim().indexOf('=');
      const name = eqIdx !== -1 ? c.trim().slice(0, eqIdx) : c.trim();
      const value = eqIdx !== -1 ? decodeURIComponent(c.trim().slice(eqIdx + 1)) : '';
      return { name: name.trim(), value };
    })
    .filter((c) => c.name);
};

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
};

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => {
          const cookies = fromCookies();
          return cookies.length > 0 ? cookies : fromStorage();
        },
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          cookiesToSet.forEach(({ name, value, options }) => {
            if (value) {
              setCookie(name, value, options as Record<string, unknown>);
              // Also persist to localStorage as fallback
              try { localStorage.setItem(`${PFX}${name}`, value); } catch {}
            } else {
              deleteCookie(name);
              try { localStorage.removeItem(`${PFX}${name}`); } catch {}
            }
          });
        },
      },
    }
  );
}
